import { API_ENDPOINT } from '../../config'
import { FeatureSwitch, FeatureSwitchMap } from '../../utils/features/featureSwitches'
import customFetch from '../../utils/customFetch'
import { FlowMetaData } from '../../@types/types'

const FEATURES_API_PATH = '/anbieten/private-anbieter/inserieren/api/configurations'

const buildQueryString = (params: Record<string, string | undefined>) => {
  const queryParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join('&')
  return queryParams ? `?${queryParams}` : ''
}

const getJsonRequestOptions = () => ({
  method: 'GET',
  headers: {
    Accept: 'application/json; charset=utf-8',
    'Content-Type': 'application/json; charset=UTF-8',
  },
  credentials: 'include' as const,
})

const getPlainTextRequestOptions = () => ({
  method: 'GET',
  headers: {
    Accept: 'text/plain',
  },
  credentials: 'include' as const,
})

const getConfigurationsUrl = (flowMetadata?: FlowMetaData) =>
  API_ENDPOINT + FEATURES_API_PATH + buildQueryString((flowMetadata || {}) as Record<string, string | undefined>)

const getFeatureConfigurationUrl = (feature: FeatureSwitch, flowMetadata?: FlowMetaData) =>
  API_ENDPOINT +
  `${FEATURES_API_PATH}/${encodeURIComponent(feature)}` +
  buildQueryString((flowMetadata || {}) as Record<string, string | undefined>)

const getTruncatedResponseBody = (body: string) => {
  return body.length > 200 ? `${body.substring(0, 200)}...` : body
}

/**
 * URL construction examples:
 * Without flow metadata: https://api.immobilien.de/anbieten/private-anbieter/inserieren/api/configurations
 * With flow metadata: https://api.immobilien.de/anbieten/private-anbieter/inserieren/api/configurations?category=APARTMENT&transaction=RENT&segmentation=PROPERTY_OWNER
 */
export const loadFeatures = async (flowMetadata?: FlowMetaData): Promise<FeatureSwitchMap> => {
  const response = await customFetch(getConfigurationsUrl(flowMetadata), getJsonRequestOptions())
  if (response.status != 200) {
    const body = await response.text()
    const truncatedBody = getTruncatedResponseBody(body)
    throw new Error(`Cannot load features, failed with ${response.status} and message ${truncatedBody}`)
  }
  return (await response.json()) as FeatureSwitchMap
}

export const loadFeature = async (feature: FeatureSwitch, flowMetadata?: FlowMetaData): Promise<boolean> => {
  const response = await customFetch(getFeatureConfigurationUrl(feature, flowMetadata), getPlainTextRequestOptions())
  if (response.status != 200) {
    const body = await response.text()
    const truncatedBody = getTruncatedResponseBody(body)
    throw new Error(`Cannot load feature ${feature}, failed with ${response.status} and message ${truncatedBody}`)
  }

  const body = (await response.text()).trim()
  if (body === 'true') {
    return true
  }
  if (body === 'false') {
    return false
  }

  throw new Error(`Cannot load feature ${feature}, unexpected response body ${body}`)
}
